/**
 * ACA General Fields (Applicant/Filer Details)
 * This is ACA-specific without nationality field
 * ID validation based solely on ID Type selection
 */

import { RuleProps } from 'types/application';
import { idNoValidation } from 'features/applicationPage/helpers';

export const SECT_TYPE = 'applicant-type-section';
export const SECT_FILER = 'filer-details';
export const SECT_APPLICANT = 'applicant-details';
export const SECT_ADDRESS = 'applicant-address';
export const SECT_COMPANY = 'company-details';

export const ACA_GENERAL_FIELDS = [
    {
        sectionID: SECT_TYPE,
        sectionName: '',
        fieldList: [
            {
                id: 'applicant-type',
                label: 'I am applying:',
                type: 'radio',
                row: true,
                span: 12,
                rule: {
                    required: true,
                    disabled: ({ isOnEdit }: RuleProps) => {
                        return isOnEdit;
                    }
                },
                options: [
                    {
                        optID: 'Applicant',
                        optLabel: 'As an Applicant'
                    },
                    {
                        optID: 'Filer',
                        optLabel: 'On behalf of an Applicant'
                    }
                ],
                defaultValue: 'Applicant',
                autoFillCallback: ({ baseValues, currentValue, myInfo }: RuleProps) => {
                    if (!myInfo) return { ...baseValues };
                    const filer = [
                        `${SECT_FILER}-id-type`,
                        `${SECT_FILER}-idno`,
                        `${SECT_FILER}-salutaion`,
                        `${SECT_FILER}-name`,
                        `${SECT_FILER}-mobile-no-main`,
                        `${SECT_FILER}-email`
                    ];

                    // Only personal fields to clear for applicant (no nationality)
                    const applicantPersonal = [
                        `${SECT_APPLICANT}-id-type`,
                        `${SECT_APPLICANT}-idno`,
                        `${SECT_APPLICANT}-salutaion`,
                        `${SECT_APPLICANT}-name`,
                        `${SECT_APPLICANT}-mobile-no-main`,
                        `${SECT_APPLICANT}-email`
                    ];
                    const values = { ...baseValues };
                    values[`${SECT_TYPE}-applicant-type`] = currentValue || '';
                    if (currentValue === 'Applicant') {
                        filer.forEach((key: string) => {
                            values[key] = '';
                        });
                        applicantPersonal.forEach((key: string, idx: number) => {
                            if ([1, 3].includes(idx)) {
                                values[key] = myInfo[key] || '';
                            } else {
                                values[key] = baseValues?.[key] || myInfo[key] || '';
                            }
                        });
                        // Address fields remain as in baseValues
                        return values;
                    }
                    filer.forEach((key: string, idx: number) => {
                        if ([1, 3].includes(idx)) {
                            values[key] = myInfo[key] || '';
                        } else {
                            values[key] = baseValues?.[key] || myInfo[key] || '';
                        }
                    });
                    // For 'Filer', preserve applicant address fields
                    applicantPersonal.forEach((key: string) => {
                        values[key] = '';
                    });
                    // Address fields (company, uen-no, postal, block-house, street, floor, unit, building) are preserved
                    return values;
                }
            }
        ]
    },
    {
        sectionID: SECT_FILER,
        sectionName: 'Filer Details',
        fieldList: [
            {
                id: 'salutaion',
                apiID: 'Salutation',
                label: 'Salutation',
                type: 'dropdown',
                rule: { required: true },
                options: [{ optID: 'Dr', optLabel: 'Dr' }]
            },
            {
                id: 'name',
                label: 'Name',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    required: true,
                    charLength: 66,
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Filer') && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'id-type',
                apiID: 'IDType',
                label: 'ID Type',
                type: 'dropdown',
                rule: {
                    required: true,
                    baseIds: [`${SECT_TYPE}-applicant-type`, `${SECT_FILER}-idno`],
                    revalidate: [`${SECT_FILER}-idno`],
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        const idNo = baseValues && (baseValues[`${SECT_FILER}-idno`] as string);
                        if (!idNo || !currentValue) return true;
                        
                        const result = idNoValidation(idNo, currentValue as string);
                        if (result !== true) {
                            return `ID Type does not match ID No. Please select the correct ID Type.`;
                        }
                        return true;
                    },
                    disabled: ({ baseValues, isSingpass, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Filer') && isSingpass && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'idno',
                label: 'ID No.',
                type: 'text',
                rule: {
                    required: true,
                    baseIds: [`${SECT_FILER}-id-type`, `${SECT_TYPE}-applicant-type`],
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        const idType = baseValues && (baseValues[`${SECT_FILER}-id-type`] as string);
                        const result = idNoValidation(currentValue as string, idType);
                        return result;
                    },
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Filer') && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'mobile-no',
                label: 'Mobile No.',
                type: 'combo',
                rule: { required: true },
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'MobileNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65'
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${SECT_FILER}-mobile-no-country-code`],
                        required: true,
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_FILER}-mobile-no-country-code`] === '65');
                            return isSG ? /^[89][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_FILER}-mobile-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                }
            },
            {
                id: 'email',
                label: 'Email Address',
                type: 'email',
                rule: { required: true, charLength: 320 }
            }
        ],
        rule: {
            baseIds: [`${SECT_TYPE}-applicant-type`],
            hidden: ({ baseValues }: RuleProps) => {
                const result = Boolean(baseValues && baseValues[`${SECT_TYPE}-applicant-type`] === 'Applicant');
                return result;
            }
        }
    },
    {
        sectionID: SECT_APPLICANT,
        sectionName: 'Applicant Details',
        fieldList: [
            {
                id: 'salutaion',
                apiID: 'Salutation',
                label: 'Salutation',
                type: 'dropdown',
                rule: { required: true },
                options: [{ optID: 'Dr', optLabel: 'Dr' }]
            },
            {
                id: 'name',
                label: 'Name',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    required: true,
                    charLength: 66,
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Applicant') && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'id-type',
                apiID: 'IDType',
                label: 'ID Type',
                type: 'dropdown',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`, `${SECT_APPLICANT}-idno`],
                    required: true,
                    revalidate: [`${SECT_APPLICANT}-idno`],
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        const idNo = baseValues && (baseValues[`${SECT_APPLICANT}-idno`] as string);
                        if (!idNo || !currentValue) return true;
                        
                        const result = idNoValidation(idNo, currentValue as string);
                        if (result !== true) {
                            return `ID Type does not match ID No. Please select the correct ID Type.`;
                        }
                        return true;
                    },
                    disabled: ({ baseValues, isSingpass, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Applicant') && isSingpass && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'idno',
                label: 'ID No.',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_APPLICANT}-id-type`, `${SECT_TYPE}-applicant-type`],
                    required: true,
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        const idType = baseValues && (baseValues[`${SECT_APPLICANT}-id-type`] as string);
                        const result = idNoValidation(currentValue as string, idType);
                        return result;
                    },
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Applicant') && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'mobile-no',
                label: 'Mobile No.',
                type: 'combo',
                rule: { required: true },
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'MobileNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65'
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${SECT_APPLICANT}-mobile-no-country-code`],
                        required: true,
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_APPLICANT}-mobile-no-country-code`] === '65');
                            return isSG ? /^[89][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_APPLICANT}-mobile-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                }
            },
            {
                id: 'email',
                label: 'Email Address',
                type: 'email',
                rule: { required: true, charLength: 320 }
            }
        ]
    },
    {
        sectionID: SECT_ADDRESS,
        sectionName: 'Address',
        rule: {
            hidden: ({ isSingpass }: RuleProps) => {
                return isSingpass !== true;
            }
        },
        fieldList: [
            {
                id: 'address-type',
                label: 'Address Type',
                apiID: 'AddressType',
                type: 'dropdown',
                options: [{ optID: 'SG', optLabel: 'Singapore' }],
                defaultValue: 'SG',
                rule: {
                    required: true,
                    disabled: true
                },
                isNextEmpty: true
            },
            {
                id: 'postal',
                label: 'Postal Code',
                apiID: 'PostalCode',
                type: 'postal',
                rule: { required: true }
            },
            {
                id: 'block-house',
                label: 'Block/House No.',
                apiID: 'BlockNumber',
                type: 'dropdown',
                rule: { required: true, charLength: 10 }
            },
            {
                id: 'street',
                label: 'Street Name',
                apiID: 'StreetName',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_ADDRESS}-postal`],
                    required: true,
                    charLength: 500
                }
            },
            {
                id: 'floor',
                label: 'Floor No.',
                apiID: 'FloorNumber',
                type: 'text',
                placeholder: 'Eg: 01, B1, 01M',
                span: 2,
                rule: { charLength: 3 }
            },
            {
                id: 'unit',
                label: 'Unit No.',
                apiID: 'UnitNumber',
                type: 'text',
                placeholder: 'Eg: #05-01, use 01',
                span: 2,
                rule: { charLength: 5 }
            },
            {
                id: 'building',
                label: 'Building Name',
                apiID: 'BuildingName',
                type: 'text',
                rule: { charLength: 500 }
            }
        ]
    },
    {
        sectionID: SECT_COMPANY,
        sectionName: 'Company Details',
        rule: {
            hidden: ({ isSingpass }: RuleProps) => {
                return isSingpass === true;
            }
        },
        fieldList: [
            {
                id: 'company',
                label: 'Name of Company',
                type: 'text',
                apiID: 'companyName',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    charLength: 100,
                    disabled: ({ myInfoAutoFill }: RuleProps) => {
                        return myInfoAutoFill;
                    },
                    required: true
                }
            },
            {
                id: 'uen-no',
                label: 'UEN No.',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    charLength: 10,
                    disabled: true
                }
            },
            {
                id: 'entity-type',
                label: 'Entity Type',
                type: 'dropdown',
                apiID: 'EntityType',
                options: [],
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    // Only visible for Corppass (not Singpass)
                    hidden: ({ isSingpass }: RuleProps) => {
                        return isSingpass === true;
                    },
                    // Disabled when auto-populated from MyInfo
                    disabled: ({ myInfoAutoFill }: RuleProps) => {
                        return myInfoAutoFill;
                    },
                    required: ({ isSingpass }: RuleProps) => {
                        return isSingpass !== true;
                    }
                }
            },
            {
                id: 'address-type',
                label: 'Address Type',
                apiID: 'AddressType',
                type: 'dropdown',
                options: [{ optID: 'SG', optLabel: 'Singapore' }],
                defaultValue: 'SG',
                rule: {
                    required: true,
                    disabled: true
                },
            },
            {
                id: 'postal',
                label: 'Postal Code',
                apiID: 'PostalCode',
                type: 'postal',
                rule: { required: true }
            },
            {
                id: 'block-house',
                label: 'Block/House No.',
                apiID: 'BlockNumber',
                type: 'dropdown',
                rule: { required: true, charLength: 10 }
            },
            {
                id: 'street',
                label: 'Street Name',
                apiID: 'StreetName',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_COMPANY}-postal`],
                    required: true,
                    charLength: 500
                }
            },
            {
                id: 'floor',
                label: 'Floor No.',
                apiID: 'FloorNumber',
                type: 'text',
                placeholder: 'Eg: 01, B1, 01M',
                span: 2,
                rule: { charLength: 3 }
            },
            {
                id: 'unit',
                label: 'Unit No.',
                apiID: 'UnitNumber',
                type: 'text',
                placeholder: 'Eg: #05-01, use 01',
                span: 2,
                rule: { charLength: 5 }
            },
            {
                id: 'building',
                label: 'Building Name',
                apiID: 'BuildingName',
                type: 'text',
                rule: { charLength: 500 }
            }
        ]
    }
];
